import { useCallback, useEffect, useState } from "react";
import { fetchDealerCoverageReviewQueue } from "../../../../services/dealerCoverageApi";
import AdminTableContainer from "../layout/AdminTableContainer";

const CATEGORY_FILTERS = [
  { value: "", label: "All categories" },
  { value: "possible_duplicate", label: "Possible duplicate" },
  { value: "possible_branch", label: "Possible branch" },
  { value: "possible_marketplace_dealer", label: "Marketplace dealer" },
  { value: "counts_toward_goal_candidate", label: "Goal candidate" },
  { value: "social_directory_needs_verification", label: "Social/directory" },
  { value: "service_or_parts_only_ambiguity", label: "Service/parts ambiguity" },
];

const DealerReviewQueuePage = ({ onClose, onOpenDealerDetail }) => {
  const [category, setCategory] = useState("");
  const [queue, setQueue] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const data = await fetchDealerCoverageReviewQueue({
        category: category || undefined,
        limit: 100,
      });
      setQueue(data.items || []);
      setTotal(data.total || 0);
      setSummary(data.summary || null);
    } catch (err) {
      setErrorMessage(err.message || "Failed to load review queue");
      setQueue([]);
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = (profileKey) => {
    if (onOpenDealerDetail) {
      onOpenDealerDetail(profileKey);
      return;
    }
    window.location.hash = `#/admin/automotive-dealer-detail/${encodeURIComponent(profileKey)}`;
  };

  return (
    <div className="admin-page automotive-dealer-review-page">
      <header className="admin-page__header">
        <div>
          <p className="admin-page__eyebrow">Automotive · Dealer Registry</p>
          <h1>Dealer review queue</h1>
          <p className="muted">Manual review items from registry heuristics. No auto-merge actions.</p>
        </div>
        <div className="admin-page__actions">
          <a className="admin-btn admin-btn--ghost" href="#/admin/automotive-dealer-coverage">
            Coverage dashboard
          </a>
          {onClose ? (
            <button type="button" className="admin-btn admin-btn--ghost" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </header>

      <section className="admin-filters admin-filters--inline">
        <select value={category} onChange={(e) => setCategory(e.target.value)}>
          {CATEGORY_FILTERS.map((f) => (
            <option key={f.value || "all"} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
        <button type="button" className="admin-btn admin-btn--secondary admin-btn--compact" onClick={load}>
          Refresh
        </button>
      </section>

      {summary ? (
        <p className="muted">
          Verify presence: {summary.verifyPresence} · Marketplace: {summary.verifyMarketplace} · Brand locator:{" "}
          {summary.verifyBrandLocator} · Duplicates: {summary.resolveDuplicate}
        </p>
      ) : null}

      {errorMessage ? <p className="admin-error">{errorMessage}</p> : null}
      {loading ? <p className="muted">Loading queue…</p> : null}

      <AdminTableContainer>
        <h3>{total} review items</h3>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Dealer</th>
              <th>City</th>
              <th>Action</th>
              <th>Confidence</th>
              <th>Categories</th>
              <th>Evidence</th>
            </tr>
          </thead>
          <tbody>
            {queue.map((item) => (
              <tr key={item.profileKey}>
                <td>
                  <button type="button" className="admin-link-btn" onClick={() => openDetail(item.profileKey)}>
                    {item.displayName}
                  </button>
                </td>
                <td>{item.primaryCity || "—"}</td>
                <td>{item.recommendedAction}</td>
                <td>{item.confidence}</td>
                <td>{(item.categories || []).join(", ")}</td>
                <td>
                  presences {item.sourcePresenceEvidence?.verified}/{item.sourcePresenceEvidence?.total}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </AdminTableContainer>
    </div>
  );
};

export default DealerReviewQueuePage;
